import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';

/**
 * Alpine component for the Terms document editor: a TipTap visual editor and
 * a raw HTML source view bound to the same `html` state, plus an iframe
 * preview. The HTML textarea is the field the form submits.
 */
export default function termsEditor({ initial = '' } = {}) {
    // Kept outside the returned object on purpose: Alpine deep-wraps every
    // property of x-data in a reactive Proxy, and TipTap/ProseMirror rely on
    // strict object identity internally (tr.before === state.doc). Storing
    // the Editor instance as a reactive property causes "Applying a
    // mismatched transaction" errors. A closure variable never gets proxied.
    let editor = null;

    return {
        html: initial ?? '',
        mode: 'visual',
        updatedAt: Date.now(),

        init() {
            editor = new Editor({
                element: this.$refs.editor,
                extensions: [
                    StarterKit.configure({
                        heading: { levels: [1, 2, 3] },
                        link: false,
                    }),
                    Link.configure({
                        openOnClick: false,
                        defaultProtocol: 'https',
                    }),
                ],
                content: this.html,
                editorProps: {
                    attributes: {
                        class: 'prose prose-sm dark:prose-invert max-w-none min-h-[520px] px-[14px] py-3 focus:outline-none',
                    },
                },
                onUpdate: ({ editor }) => {
                    this.html = editor.isEmpty ? '' : editor.getHTML();
                    this.refreshPreview();
                },
                onTransaction: () => {
                    // Bump so toolbar active states re-evaluate.
                    this.updatedAt = Date.now();
                },
            });
            this.refreshPreview();
        },

        destroy() {
            editor?.destroy();
            editor = null;
        },

        setMode(mode) {
            if (mode === this.mode) {
                return;
            }
            if (mode === 'visual') {
                // Round-trip manual HTML edits into the visual editor.
                editor.commands.setContent(this.html);
            }
            this.mode = mode;
        },

        onSourceInput() {
            this.refreshPreview();
        },

        refreshPreview() {
            this.$refs.preview.srcdoc = this.html || '';
        },

        command(name, attributes = undefined) {
            const chain = editor.chain().focus();
            if (typeof chain[name] !== 'function') {
                return;
            }
            (attributes === undefined ? chain[name]() : chain[name](attributes)).run();
        },

        isActive(name, attributes = {}, updatedAt = null) {
            void updatedAt;
            return editor?.isActive(name, attributes) ?? false;
        },

        setLink() {
            const previous = editor.getAttributes('link').href ?? '';
            const url = window.prompt('Link URL', previous);
            if (url === null) {
                return;
            }
            if (url.trim() === '') {
                editor.chain().focus().unsetLink().run();
                return;
            }
            editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
        },
    };
}
